import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { PageStatCards } from "@/components/PageStatCards";
import { TableToolbar } from "@/components/TableToolbar";
import { RowActions } from "@/components/RowActions";
import { DetailDrawer, DrawerSection, DrawerField } from "@/components/DetailDrawer";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";
import { CompanyField } from "@/components/CompanyField";


export default function Warehouses() {
  const { can } = usePermissions();
  const canCreateWarehouse = can("WAREHOUSES", "create");
  const { companies } = useCompany() || {};
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/warehouses");
      setWarehouses(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const companyName = (id) => companies?.find((c) => c.id === id)?.name || id;

  const shown = useMemo(() => {
    if (!search) return warehouses;
    const q = search.toLowerCase();
    return warehouses.filter((w) =>
      (w.code || "").toLowerCase().includes(q) ||
      (w.name || "").toLowerCase().includes(q) ||
      (w.city || "").toLowerCase().includes(q)
    );
  }, [warehouses, search]);

  return (
    <div className="space-y-6" data-testid="warehouses-page">
      <PageHeader
        title="Warehouses"
        description="Site stores and depots. Stock is tracked per item, per warehouse."
        action={canCreateWarehouse && <CreateWarehouseDialog companies={companies} onCreated={load} />}
      />
      <PageStatCards loading={loading} stats={[
        { label: "Total Warehouses", value: warehouses.length, color: "blue" },
        { label: "Companies Covered", value: new Set(warehouses.map((w) => w.company_id)).size, color: "orange" },
        { label: "With Location", value: warehouses.filter((w) => w.city).length, color: "green" },
        { label: "Showing", value: shown.length, color: "gray" },
      ]} />
      <TableToolbar
        search={search}
        onSearch={setSearch}
        csvData={shown.map((w) => ({ Code: w.code, Name: w.name, Company: companyName(w.company_id), City: w.city || "" }))}
        csvFilename="warehouses.csv"
      />
      <div className="rounded-xl border border-border bg-card">
        {!loading && warehouses.length === 0 ? (
          <EmptyState title="No warehouses yet" testId="warehouses-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>City</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((w) => (
                <TableRow key={w.id} data-testid={`warehouse-row-${w.code}`}>
                  <TableCell className="font-mono text-xs text-foreground">{w.code}</TableCell>
                  <TableCell className="text-foreground">{w.name}</TableCell>
                  <TableCell className="text-muted-foreground">{companyName(w.company_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{w.city || "—"}</TableCell>
                  <TableCell>
                    <RowActions testId={`warehouse-actions-${w.code}`} onView={() => setSelected(w)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <DetailDrawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.name}
        subtitle={`Warehouse · ${selected?.code}`}
      >
        <DrawerSection label="Location Details">
          <DrawerField label="Code" value={selected?.code} mono />
          <DrawerField label="Name" value={selected?.name} />
          <DrawerField label="Company" value={selected ? companyName(selected.company_id) : null} />
          <DrawerField label="City / Location" value={selected?.city} />
        </DrawerSection>
      </DetailDrawer>
    </div>
  );
}

function CreateWarehouseDialog({ companies, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", code: "", name: "", city: "" });
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/inventory/warehouses", form);
      toast.success(`Warehouse ${form.code} created`);
      setForm({ company_id: "", code: "", name: "", city: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-warehouse-button"><Plus className="h-4 w-4" /> New Warehouse</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Warehouse</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-warehouse-form">
          <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="warehouse-company-select" />
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input placeholder="Automatic if blank" data-testid="warehouse-code-input" value={form.code} onChange={set("code")} className="mt-1.5" /></div>
            <div><Label>Name</Label><Input required data-testid="warehouse-name-input" value={form.name} onChange={set("name")} className="mt-1.5" /></div>
          </div>
          <div><Label>City</Label><Input data-testid="warehouse-city-input" value={form.city} onChange={set("city")} className="mt-1.5" /></div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-warehouse-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
